import React from 'react';
import { useTranslation } from 'react-i18next';

import type { StatsMoneyFlow } from '../../../../mock-data/types';
import { StatCard } from '../StatCard';
import { PrimaryStat } from '../PrimaryStat';
import { SplitStat } from '../SplitStat';
import { Inner, Divider, SplitRow } from './MoneyFlow.styles';

export type MoneyFlowProps = {
  data: StatsMoneyFlow;
  /** While true, the figures show placeholders (the endpoint is loading). */
  loading?: boolean;
  /** Reveal sections whose data isn't available yet (the `show_in_progress` flag). */
  showInProgress?: boolean;
};

/** "Money Flow" — the Deposits lead with Withdrawals / Player Balance split
 *  stats (no chart). The time range is driven globally from the page header. */
export const MoneyFlow = ({ data, loading = false, showInProgress = false }: MoneyFlowProps): React.ReactElement => {
  const { t } = useTranslation();
  // Player Balance has no API field yet, so it stays hidden behind the in-progress
  // flag (rather than shipping a bare `?`). It reappears automatically once a field
  // backs it (missing flips false).
  const showPlayerBalance = !data.playerBalance.missing || showInProgress;
  return (
    <StatCard title={t('Money Flow')} sub={t('The movement of real cash')}>
      <Inner>
        <PrimaryStat data={data.deposits} loading={loading} />
        <Divider />
        <SplitRow>
          <SplitStat data={data.withdrawals} loading={loading} />
          {showPlayerBalance ? <SplitStat data={data.playerBalance} loading={loading} /> : null}
        </SplitRow>
      </Inner>
    </StatCard>
  );
};

export default MoneyFlow;
